import { useState } from 'react';
import { cancelAll, isOpen, type Trading } from '../api/trading';
import { money } from '../format';

const pnl = (v: string) => `${Number(v) >= 0 ? '+' : '−'}${money(Math.abs(Number(v)))}`;

/** Environment and money at a glance, plus the kill switch. Always visible, so LIVE can never be mistaken for PAPER. */
export function AccountBar({ trading, notify }: { trading: Trading; notify: (text: string, tone?: 'up' | 'down' | 'info') => void }) {
  const [confirming, setConfirming] = useState(false);
  const s = trading.snapshot, a = s?.account;
  const open = (s?.orders ?? []).filter(isOpen).length;
  const env = trading.status === 'connected' && a ? a.environment : 'none';
  const killAll = async () => {
    setConfirming(false);
    try { const r = await cancelAll(); notify(`Cancelled ${r.cancelled} open order${r.cancelled === 1 ? '' : 's'}`, 'info'); trading.refresh(); }
    catch (e) { notify(e instanceof Error ? e.message : 'Cancel all failed', 'down'); }
  };
  return <div className="account" data-testid="account">
    <span className={`env ${env}`} title={trading.status === 'connected' ? undefined : trading.detail}>{env === 'none' ? (trading.status === 'connecting' ? 'CHECKING' : 'NOT CONNECTED') : env.toUpperCase()}</span>
    {a?.problems?.length ? <strong className="blocked" role="alert" data-testid="problems" title={a.problems.join('\n')}>New orders blocked: records don’t reconcile</strong> : null}
    <span>Cash<b>{a ? money(a.cash) : '—'}</b></span>
    <span>In positions<b>{a ? money(a.positions_value) : '—'}</b></span>
    <span className="optional">On order<b>{a ? money(a.reserved) : '—'}</b></span>
    <span>Today<b className={a ? (Number(a.realized_today) >= 0 ? 'up' : 'down') : ''}>{a ? pnl(a.realized_today) : '—'}</b></span>
    <span className="optional">Open P&amp;L<b className={a ? (Number(a.open_pnl) >= 0 ? 'up' : 'down') : ''}>{a ? pnl(a.open_pnl) : '—'}</b></span>
    <button className="kill" disabled={!open || trading.status !== 'connected'} onClick={() => setConfirming(true)} data-testid="cancel-all">Cancel all ({open})</button>
    {confirming && <>
      <div className="scrim modal-scrim" onClick={() => setConfirming(false)}/>
      <div className="confirm small" role="dialog" aria-modal="true" aria-label="Cancel all orders">
        <div className="confirm-body"><h3>Cancel all {open} open order{open === 1 ? '' : 's'}?</h3><p className="faint">Positions you already hold are not affected.</p></div>
        <div className="confirm-actions"><button className="tool" onClick={() => setConfirming(false)} autoFocus>Keep orders</button><button className="go sell" onClick={killAll}>Cancel all</button></div>
      </div>
    </>}
  </div>;
}

/** Phones hide the account bar's figures, so the sticky footer carries them (plan 020). */
export function PhoneAccount({ trading }: { trading: Trading }) {
  const a = trading.status === 'connected' ? trading.snapshot?.account : undefined;
  const tone = (v?: string) => v == null ? '' : Number(v) >= 0 ? 'up' : 'down';
  return <div className="phone-account" data-testid="phone-account">
    <span>Cash<b>{a ? money(a.cash) : '—'}</b></span>
    <span>In positions<b>{a ? money(a.positions_value) : '—'}</b></span>
    <span>Open P&amp;L<b className={tone(a?.open_pnl)}>{a ? pnl(a.open_pnl) : '—'}</b></span>
    <span>Today<b className={tone(a?.realized_today)}>{a ? pnl(a.realized_today) : '—'}</b></span>
  </div>;
}
